/**
 * Action Register authority (13-05): who sees what, and who may do which verb. Pure functions.
 *
 *   - `isRegulatoryDecision(row)`: the fail-safe classifier (D-13-00-2);
 *   - `canDoVerb(verb, row, actor, ctx)`: the verb permission map (D-13-00-5);
 *   - `lensFor(actor)`: the row predicates behind "my queue", the verify and release queues, and
 *     the briefing's "my patch" register.
 *
 * They carry the two safeguards Andy confirmed explicitly: NO self-verification, and regulatory
 * decisions by Oversight ONLY (through the regulatory panel). They also enforce the release gate
 * (09.1 §8b, Andy 9 Sep): nobody acts on, or is queued, an unreleased row, except Oversight to
 * release or retire it.
 *
 * This is a separate model from `ROLE_REGISTER_PERMISSIONS` in roles.ts, whose authority floor
 * stays untouched (RESEARCH anti-pattern 1). Rows are lensed by `Owner` / `OwnerRole` only, never
 * by `scope.ts` Portfolio (which returns everything while the business-unit map is empty).
 *
 * State-machine refusals (wrong status for a verb) are 13-04's `VerbError`; this module answers
 * only "may this person do it". The UI should ask both. One exception: `retire` refuses a row that
 * is no longer open (`not-open`, ISS-212), because Today offers it on any released card.
 *
 * No React, no I/O.
 */

import { OPEN_STATUSES, REGULATORY } from '../config/actionRegister';
import type { AppRole } from '../types/auth';
import type { RegisterKey } from '../types/qms';
import type { ActionKind, ActionRow, ActionVerb, Actor, OwnerRole } from '../types/actionRegister';

// ---------------------------------------------------------------------------
// Owner roles → App Roles (D-13-00-5)
// ---------------------------------------------------------------------------

/**
 * The QMS App Role (roles.ts `AppRole`) that holds each `OwnerRole`, or `null` when none does.
 * The five with no App Role (`Admin` / `Board` / `QCAC` / `Wellness` / `DAG`) fall to Oversight.
 * A `Record` over `OwnerRole`, so a new owner role does not compile until it is mapped here.
 */
export const OWNER_ROLE_APP_ROLE: Readonly<Record<OwnerRole, AppRole | null>> = {
  Oversight: 'Oversight',
  CareManager: 'CareManager',
  HRWHS: 'HRWHS',
  Finance: 'Finance',
  Admin: null,
  Board: null,
  QCAC: null,
  Wellness: null,
  DAG: null,
};

/** The owner roles with no App Role: their rows fall to Oversight's lens. */
export const FALLBACK_OWNER_ROLES: readonly OwnerRole[] = (
  Object.keys(OWNER_ROLE_APP_ROLE) as OwnerRole[]
).filter((role) => OWNER_ROLE_APP_ROLE[role] === null);

// ---------------------------------------------------------------------------
// Small predicates
// ---------------------------------------------------------------------------

function isBlank(value: string | null | undefined): boolean {
  return value == null || value.trim() === '';
}

/** UPNs and audit Author emails compare case-insensitively, ignoring surrounding space (D-13-05-5; also 13-22). */
export function sameUpn(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

/** The completer's UPN when the caller resolved one; `null` when blank or absent. */
function completerOf(completedByUpn: string | undefined): string | null {
  return isBlank(completedByUpn) ? null : (completedByUpn ?? null);
}

/** The actor holds the Oversight App Role (also used by 13-22's `recordAuthority`). */
export function isOversight(actor: Actor): boolean {
  return actor.roles.includes('Oversight');
}

function isReleased(row: ActionRow): boolean {
  return !isBlank(row.releasedAt);
}

function isOpen(row: ActionRow): boolean {
  return (OPEN_STATUSES as readonly string[]).includes(row.status);
}

/**
 * The owner rule (accept / done / notMine / an Ordinary decide):
 *   - the actor IS the named Owner (lookup ids match; an unresolved actor never matches); or
 *   - `Owner` is empty and the actor holds the App Role for `OwnerRole`; or
 *   - `OwnerRole` has no App Role and the actor is Oversight (whoever is named: a named owner in
 *     a no-App-Role role cannot sign in to the QMS at all).
 * Oversight does not otherwise act on other people's rows; it sees them.
 */
function mayActAsOwner(row: ActionRow, actor: Actor): boolean {
  const owner = isBlank(row.ownerLookupId) ? null : row.ownerLookupId;
  if (owner !== null && !isBlank(actor.lookupId) && owner === actor.lookupId) return true;
  // A runtime OwnerRole outside the vocabulary maps to undefined: fail closed.
  const appRole: AppRole | null | undefined = OWNER_ROLE_APP_ROLE[row.ownerRole];
  if (appRole === undefined) return false;
  if (appRole === null) return isOversight(actor);
  return owner === null && actor.roles.includes(appRole);
}

// ---------------------------------------------------------------------------
// isRegulatoryDecision (D-13-00-2)
// ---------------------------------------------------------------------------

export interface RegulatoryClass {
  regulatory: boolean;
  /**
   * Why: `'classified'` (DecisionClass set), `'not a decision'`, `'no regulatory signal'`, or the
   * matching rule in the Python mirror's own words (`'ParentType=Incident'`, `'Source=Incident'`,
   * `'ActionType=<token>'`, `'<Column>~/<matched text>/'`).
   */
  because: string;
}

/**
 * The signal rule for an unclassified Decision, or `null`. Mirrors `regulatory_rule()` in
 * Harmony-QMS/build/action-register/extend_action_register_v13.py EXACTLY (precedence and the
 * returned text): ParentType, then Source, then the ActionType allow-list, then the pattern over
 * Title, Options, ActionType. The two move together.
 */
function regulatoryRule(row: ActionRow): string | null {
  if (row.parentType === 'Incident') return 'ParentType=Incident';
  if (row.source === 'Incident') return 'Source=Incident';
  const actionType = row.actionType ?? '';
  if ((REGULATORY.actionTypes as readonly string[]).includes(actionType)) return `ActionType=${actionType}`;
  const columns: ReadonlyArray<readonly [string, string | null]> = [
    ['Title', row.title],
    ['Options', row.options],
    ['ActionType', row.actionType],
  ];
  for (const [column, text] of columns) {
    const match = REGULATORY.pattern.exec(text ?? '');
    if (match) return `${column}~/${match[0]}/`;
  }
  return null;
}

/**
 * Is this row a regulatory Decision? Fail-safe: a classified row (`DecisionClass`) is what it
 * says; an UNCLASSIFIED Decision with any regulatory signal is regulatory. The only way a
 * signalled Decision is ordinary is `DecisionClass = Ordinary`, a human or card call. Never
 * "Ordinary when unsure". Only `Kind = Decision` rows can be regulatory.
 */
export function isRegulatoryDecision(row: ActionRow): RegulatoryClass {
  if (row.kind !== 'Decision') return { regulatory: false, because: 'not a decision' };
  if (row.decisionClass === 'Regulatory') return { regulatory: true, because: 'classified' };
  if (row.decisionClass === 'Ordinary') return { regulatory: false, because: 'classified' };
  const rule = regulatoryRule(row);
  return rule === null
    ? { regulatory: false, because: 'no regulatory signal' }
    : { regulatory: true, because: rule };
}

// ---------------------------------------------------------------------------
// canDoVerb (D-13-00-5)
// ---------------------------------------------------------------------------

/**
 * Every refusal reason, as stable codes the UI phrases kindly (e.g. `no-self-verification` →
 * "This needs someone else to verify — you completed it").
 */
export const AUTHORITY_REASONS = [
  /** The row has no `ReleasedAt`: only Oversight's release / retire touch it (09.1 §8b). */
  'not-released',
  /** Not the Owner, not an unowned row of my role, not an Oversight-fallback row. */
  'not-owner',
  /** verify / reject / release / retire need Oversight. */
  'oversight-only',
  /** A regulatory Decision: Oversight decides it, through the regulatory panel. */
  'regulatory-oversight-only',
  /** The actor completed this row and may not verify or reject it. */
  'no-self-verification',
  /** reopen: someone else completed it. */
  'not-completer',
  /** verify / reject / reopen without a resolved completer: no proof either way, so refused. */
  'completer-unknown',
  /** Not an Action Register verb (a runtime string). Refused, never allowed. */
  'unknown-verb',
  /**
   * retire (ISS-212): the row is no longer open (Done / Verified / Rejected / NotMine / Retired).
   * A Done row goes to Verify and a NotMine row to Reassign; neither is retired.
   */
  'not-open',
] as const;

export type AuthorityReason = (typeof AUTHORITY_REASONS)[number];

export type AuthorityResult = { ok: true } | { ok: false; reason: AuthorityReason };

export interface AuthorityContext {
  /**
   * The UPN of whoever set `Status = Done`, resolved by the caller from that audit row (its
   * Author email is the fallback). Needed by verify / reject / reopen; absent → refused.
   */
  completedByUpn?: string;
  /**
   * The caller's `isRegulatoryDecision(row).regulatory`. It can only RAISE the classification:
   * the row is classified here as well, and either saying "regulatory" makes it regulatory.
   */
  regulatory?: boolean;
}

type VerbRule = (row: ActionRow, actor: Actor, ctx: AuthorityContext) => AuthorityResult;

const OK: AuthorityResult = { ok: true };
const refuse = (reason: AuthorityReason): AuthorityResult => ({ ok: false, reason });

const ownerRule: VerbRule = (row, actor) => (mayActAsOwner(row, actor) ? OK : refuse('not-owner'));

const oversightRule: VerbRule = (_row, actor) => (isOversight(actor) ? OK : refuse('oversight-only'));

/** The completer limb shared by verify / reject: a known completer who is not the actor. */
function completerLimb(actor: Actor, ctx: AuthorityContext): AuthorityResult {
  const completer = completerOf(ctx.completedByUpn);
  if (completer === null) return refuse('completer-unknown');
  return sameUpn(completer, actor.upn) ? refuse('no-self-verification') : OK;
}

/**
 * verify / reject on an Action Register row: Oversight (D-13-05-7: the AR has no VerifierRole
 * column), and never the person who completed the row.
 */
const verifierRule: VerbRule = (_row, actor, ctx) => {
  if (!isOversight(actor)) return refuse('oversight-only');
  return completerLimb(actor, ctx);
};

/** decide: Oversight only when regulatory (fail-safe); otherwise the owner rule. */
const decideRule: VerbRule = (row, actor, ctx) => {
  const regulatory = ctx.regulatory === true || isRegulatoryDecision(row).regulatory;
  if (regulatory) return isOversight(actor) ? OK : refuse('regulatory-oversight-only');
  return ownerRule(row, actor, ctx);
};

/**
 * retire: Oversight only (D-13-00-5), and only while the row is open (Proposed / Accepted /
 * InProgress), released or not. ISS-212: a released, still-open row can be retired from its Today
 * card; an unreleased one is still retired in the release sitting. The status limb is asked here as
 * well as by the 13-04 builder, so the card never offers a retire the builder would refuse. No
 * completer rule: retiring is not a verification.
 */
const retireRule: VerbRule = (row, actor) => {
  if (!isOversight(actor)) return refuse('oversight-only');
  return isOpen(row) ? OK : refuse('not-open');
};

/** reopen: only the actor who completed it. */
const completerRule: VerbRule = (_row, actor, ctx) => {
  const completer = completerOf(ctx.completedByUpn);
  if (completer === null) return refuse('completer-unknown');
  return sameUpn(completer, actor.upn) ? OK : refuse('not-completer');
};

/**
 * The permission map, keyed by verb. A `Record` over `ActionVerb`, so adding a verb to
 * `ACTION_VERBS` does not compile until its rule is set here. `reassign` (ISS-186, D-13-R-1) is
 * Oversight only and stays behind the release gate: a `NotMine` row was released before it was
 * handed back.
 */
const VERB_RULES: Readonly<Record<ActionVerb, VerbRule>> = {
  accept: ownerRule,
  done: ownerRule,
  notMine: ownerRule,
  verify: verifierRule,
  reject: verifierRule,
  decide: decideRule,
  release: oversightRule,
  retire: retireRule,
  reopen: completerRule,
  reassign: oversightRule,
};

/** The only verbs allowed on an unreleased row (still Oversight only, by their rule). */
const RELEASE_GATE_EXEMPT: ReadonlySet<ActionVerb> = new Set<ActionVerb>(['release', 'retire']);

/**
 * The verbs that skip the release gate on a row that is already `Done` (their own rule still runs:
 * Oversight, no self-verification, a known completer).
 *   - `verify`: ISS-187 / D-13-R-2 (Andy, 4 Oct 2026, option (a)). The gate stops the poller carding
 *     an owner cold, and a Done row has no owner card left to send.
 *   - `reject`: ISS-198 / D-13-R-7 (Andy, 4 Oct 2026). Verify's twin on the same row, so an
 *     unreleased Done row can be returned as well as passed. E3 applies as normal.
 * `reopen` stays gated: it is the completer's own same-day undo, not a verification.
 */
const DONE_ROW_GATE_EXEMPT: ReadonlySet<ActionVerb> = new Set<ActionVerb>(['verify', 'reject']);

function passesReleaseGate(verb: ActionVerb, row: ActionRow): boolean {
  if (RELEASE_GATE_EXEMPT.has(verb) || isReleased(row)) return true;
  return DONE_ROW_GATE_EXEMPT.has(verb) && row.status === 'Done';
}

/**
 * May `actor` do `verb` on `row`? Authority only: the 13-04 builder still refuses an invalid
 * transition. Order: unknown verb → the release gate (verify and reject on a Done row are exempt,
 * D-13-R-2 / D-13-R-7) → the verb's rule.
 */
export function canDoVerb(
  verb: ActionVerb,
  row: ActionRow,
  actor: Actor,
  ctx: AuthorityContext = {},
): AuthorityResult {
  if (!Object.prototype.hasOwnProperty.call(VERB_RULES, verb)) return refuse('unknown-verb');
  if (!passesReleaseGate(verb, row)) return refuse('not-released');
  return VERB_RULES[verb](row, actor, ctx);
}

// ---------------------------------------------------------------------------
// Verification Log verifiers (ISS-195 / D-13-R-6)
// ---------------------------------------------------------------------------

/**
 * May `actor` give a verdict on a Verification Log row whose `VerifierRole` is `verifierRole`?
 * D-13-R-6 (Andy, 4 Oct 2026): Oversight, or a holder of the App Role for the row's
 * `VerifierRole`, so the people E4 nudges can act on what they are nudged about. A blank
 * `VerifierRole`, a role with no App Role (Admin / Board / QCAC / Wellness / DAG) or an unknown
 * runtime value is Oversight only (fail closed). Verification Log rows only: Action Register
 * verify stays Oversight-only (D-13-05-7).
 */
export function mayVerifyEvidence(verifierRole: OwnerRole | null | undefined, actor: Actor): boolean {
  if (isOversight(actor)) return true;
  if (isBlank(verifierRole)) return false;
  const appRole: AppRole | null | undefined = OWNER_ROLE_APP_ROLE[verifierRole as OwnerRole];
  return appRole != null && actor.roles.includes(appRole);
}

/**
 * The completer limb of `verify` on an Action Register row, WITHOUT its Oversight rule: verify's
 * release gate (an unreleased `Done` row is exempt, D-13-R-2), a known completer, and never the
 * completer. Used for Verification Log evidence whose `RecordRef` is a completed action
 * (D-13-11-3, as amended by D-13-R-6): who may give the VL verdict is `mayVerifyEvidence`'s
 * question, and this one asks only "is it someone other than whoever completed the action?".
 */
export function completerGate(row: ActionRow, actor: Actor, ctx: AuthorityContext = {}): AuthorityResult {
  if (!passesReleaseGate('verify', row)) return refuse('not-released');
  return completerLimb(actor, ctx);
}

// ---------------------------------------------------------------------------
// lensFor
// ---------------------------------------------------------------------------

/** The briefing's "my patch" register: a five-register key, or the Action Register itself. */
export type PatchRegister = Extract<RegisterKey, 'incidents' | 'complaints'> | 'actions';

export interface ActionLens {
  /**
   * Released, open, and mine to act on: owner is me, or unowned and my role holds `OwnerRole`,
   * or an Oversight-fallback role for an Oversight actor. A regulatory Decision is in every
   * Oversight queue and in no other lens's queue. Agrees with `canDoVerb` by construction.
   */
  myQueue: (row: ActionRow) => boolean;
  /**
   * Oversight only: `Done` Actions / Approvals, released or not (D-13-R-2 / D-13-R-7: verify and
   * reject are exempt from the release gate on a Done row). When the caller knows who completed
   * the row, a row I completed is left out (I could not verify it anyway).
   */
  myVerifications: (row: ActionRow, completedByUpn?: string) => boolean;
  /**
   * Oversight only (ISS-186, D-13-R-1): released rows an owner handed back (`Status = NotMine`),
   * waiting to be reassigned. Agrees with `canDoVerb('reassign')`.
   */
  handedBack: (row: ActionRow) => boolean;
  /** Oversight only: open and unreleased. */
  releaseQueue: (row: ActionRow) => boolean;
  /** CareManager / HRWHS → incidents, Finance → complaints, Oversight → actions; else null. */
  myPatchRegister: PatchRegister | null;
}

const VERIFIABLE_KINDS: readonly ActionKind[] = ['Action', 'Approval'];

function patchRegisterFor(actor: Actor): PatchRegister | null {
  if (isOversight(actor)) return 'actions';
  if (actor.roles.includes('CareManager')) return 'incidents';
  if (actor.roles.includes('HRWHS')) return 'incidents'; // the WHS lens
  if (actor.roles.includes('Finance')) return 'complaints';
  return null;
}

export function lensFor(actor: Actor): ActionLens {
  const oversight = isOversight(actor);
  return {
    myQueue: (row) => {
      if (!isReleased(row) || !isOpen(row)) return false;
      if (isRegulatoryDecision(row).regulatory) return oversight;
      return mayActAsOwner(row, actor);
    },
    myVerifications: (row, completedByUpn) => {
      if (!oversight || row.status !== 'Done') return false;
      if (!VERIFIABLE_KINDS.includes(row.kind)) return false;
      const completer = completerOf(completedByUpn);
      return completer === null || !sameUpn(completer, actor.upn);
    },
    handedBack: (row) => oversight && isReleased(row) && row.status === 'NotMine',
    releaseQueue: (row) => oversight && isOpen(row) && !isReleased(row),
    myPatchRegister: patchRegisterFor(actor),
  };
}
